import {
  State,
  Action,
  StateContext,
  Selector,
  createSelector,
} from '@ngxs/store';
import { ParticlesStateModel } from './particles.state.model';
import { ParticleActions } from './particles.actions';
import { ParticleSystem } from '../../models/particles/particle-system-types';
import { TweenParticleLookup } from 'src/app/shared/tween-particle-lookup';
import { ParticleIdUtils } from 'src/app/shared/particle-id-utils';
import { mapValues } from 'lodash';
import { Injectable } from '@angular/core';

declare const immer;

@State<ParticlesStateModel>({
  name: 'engineParticles',
  defaults: {
    particleSelection: {},
    particleSystems: {},
    visibleParticleSystems: {},
    assignedParticles: {},
    tweenParticleLookup: {},
  },
})
@Injectable()
export class EngineParticlesState {
  static particleSystemIdExists(id: string) {
    return createSelector(
      [EngineParticlesState],
      (state: ParticlesStateModel) => {
        return state.particleSystems[id] !== undefined;
      }
    );
  }

  static particleSystemsForChapter(chapterId: string) {
    return createSelector([EngineParticlesState], (state: ParticlesStateModel): ParticleSystem[]  => {
      const assignedChapterParticleIds = state.assignedParticles[chapterId] || [];
      return assignedChapterParticleIds.map(particleId => state.particleSystems[particleId]);
    });
  }

  static getVisibleParticleSystesmForChapter(chapterId: string) {
    return createSelector([EngineParticlesState], (state: ParticlesStateModel): string[] => {
      const chapterParticles = state.assignedParticles[chapterId] || [];
      const chapterParticlesVisibility = state.visibleParticleSystems[chapterId] || {};

      // get list of particles not explcitity hidden
      return chapterParticles
        .filter(id => chapterParticlesVisibility[id] !== false);
    });
  }

  @Selector()
  static getTweenParticleLookup(state: ParticlesStateModel) {
    return state.tweenParticleLookup;
  }

  @Selector()
  static getParticleSystems(state: ParticlesStateModel) {
    return state.particleSystems;
  }

  @Selector()
  static getVisisbleParticleSystems(state: ParticlesStateModel) {
    return state.visibleParticleSystems;
  }

  @Selector()
  static getParticleSystemsList(state: ParticlesStateModel) {
    return Object.values(state.particleSystems);
  }

  @Selector()
  static getAssignedParticleSystems(state: ParticlesStateModel) {
    return state.assignedParticles;
  }

  @Selector()
  static getSelectedParticleSystemId(state: ParticlesStateModel) {
    return state.particleSelection.selectedParticle;
  }

  @Selector()
  static getSelectedParticleSystem(state: ParticlesStateModel) {
    const { particleSystems, particleSelection, } = state;
    return particleSystems[particleSelection.selectedParticle];
  }

  @Selector()
  static getSelectedParticleConstraintIndex(state: ParticlesStateModel) {
    return state.particleSelection.selectedConstraint;
  }

  @Selector()
  static getSelectedParticleConstraint(state: ParticlesStateModel) {
    const system = EngineParticlesState.getSelectedParticleSystem(state);
    const index = state.particleSelection.selectedConstraint;
    return system ? system.constraints[index] : null;
  }

  @Selector()
  static getSelectedParticleCreatorIndex(state: ParticlesStateModel) {
    return state.particleSelection.selectedCreator;
  }
  
  @Selector()
  static getSelectedParticleCreator(state: ParticlesStateModel) {
    const system = EngineParticlesState.getSelectedParticleSystem(state);
    const index = state.particleSelection.selectedCreator;
    return system ? system.creators[index] : null;
  }

  @Selector()
  static getSelectedParticleUpdaterIndex(state: ParticlesStateModel) {
    return state.particleSelection.selectedUpdater;
  }

  @Selector()
  static getSelectedParticleUpdater(state: ParticlesStateModel) {
    const system = EngineParticlesState.getSelectedParticleSystem(state);
    const index = state.particleSelection.selectedUpdater;
    return system ? system.updaters[index] : null;
  }


  @Action(ParticleActions.ApplyPatch)
  applyPatch(context: StateContext<ParticlesStateModel>, action: ParticleActions.ApplyPatch) {
    const nextState = immer.applyPatches(context.getState(), action.patches);
    context.patchState(nextState);
  }

  @Action(ParticleActions.SetParticleState)
  setParticleState(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.SetParticleState
  ) {
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        
        draft.particleSystems = action.particleSystems.reduce((acc, particleSystem) => {
          acc[particleSystem.id] = particleSystem;
          return acc;
        }, {});

        draft.assignedParticles = action.assignedParticles;
        draft.tweenParticleLookup = action.tweenParticleLookup;

        // reset visibility
        draft.visibleParticleSystems = {};
      })
    );
  }

  @Action(ParticleActions.SetParticleSystemsVisible)
  setParticlesVisible(
    context: StateContext<ParticlesStateModel>,
    { visible, chapterId }: ParticleActions.SetParticleSystemsVisible
  ) {
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        // patch particle visiblity for chapter
        const chapterParticleIds = draft.assignedParticles[chapterId] || [];
        const chapterParticleVisiblity = chapterParticleIds
          .reduce((acc, id) => {
            acc[id] = visible;
            return acc;
          }, {});

        draft.visibleParticleSystems = {
          ...draft.visibleParticleSystems,
          [chapterId]: chapterParticleVisiblity
        };
      })
    );
  }

  @Action(ParticleActions.SetParticleSystemVisible)
  setParticleVisible(
    context: StateContext<ParticlesStateModel>,
    { id, visible, chapterId }: ParticleActions.SetParticleSystemVisible
  ) {
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        // patch system visiblity for chapter
        const visibleParticleSystemsForChapter = draft.visibleParticleSystems[chapterId] || {};
        draft.visibleParticleSystems[chapterId] = {
          ...visibleParticleSystemsForChapter,
          [id]: visible
        };
      })
    );
  }

  @Action(ParticleActions.NewParticleSystem)
  newParticleSystem(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.NewParticleSystem
  ) {
    const { particleSystem, chapterId } = action;
    const particleSystemId = particleSystem.id;


    const [nextState, patches, inversePatches] = immer.produceWithPatches(context.getState(), (draft: ParticlesStateModel) => {
      // TODO: what happens if particle Id collides?

      // add
      draft.particleSystems[particleSystemId] = particleSystem;

      // assign to chapter
      const chapterParticleSystemIds = draft.assignedParticles[chapterId] || [];
      draft.assignedParticles[chapterId] = [
        ...chapterParticleSystemIds,
        particleSystemId
      ];

      // Do not modify the lookup.
    });

    context.patchState(nextState);

    // save undo support for a later iteration
  }
  @Action(ParticleActions.SetParticleSystemId)
  setParticleSystemId(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.SetParticleSystemId
  ) {

    const idIsUsed = context.getState().particleSystems[action.newId] !== undefined;
    if (idIsUsed) { return; }


    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        draft.particleSelection.selectedParticle =

          // selected particle
          draft.particleSelection.selectedParticle === action.oldId
            ? action.newId
            : draft.particleSelection.selectedParticle;
           

          // particle systems
          draft.particleSystems[action.newId] = {
            ...draft.particleSystems[action.oldId],
            id: action.newId
          };
          delete draft.particleSystems[action.oldId];


          // assigned particles
          Object.entries(draft.assignedParticles).forEach(([chapterId, particles]) => {
            draft.assignedParticles[chapterId] = particles.map(id => id === action.oldId ? action.newId : id);
          });

          
          // tween particle lookup
          Object.entries(draft.tweenParticleLookup).forEach(([chapterId, lookup]) => {
            draft.tweenParticleLookup[chapterId] = mapValues(lookup, (value, key, object) => {
              return value === action.oldId ? action.newId : value;
            });
          });
      })
    );
  }

  @Action(ParticleActions.DuplicateParticleSystem)
  duplicateParticleSystem(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.DuplicateParticleSystem
  ) {
    const { id, targetChapterId } = action;
    // generate new id.
    const newId = ParticleIdUtils.generateParticleId(id);

    context.patchState(
      // will support undo later
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {

        // duplicate
        draft.particleSystems[newId] = JSON.parse(
          JSON.stringify(draft.particleSystems[id])
        );

        // set Id
        draft.particleSystems[newId].id = newId;

        // assign to chapter
        draft.assignedParticles[targetChapterId].push(newId);

        // modify the lookup if the original particle system has an entry in the lookup.
        const originalId = ParticleIdUtils.getParticleIdRoot(id);
        const originalInLookup =
          draft.tweenParticleLookup[targetChapterId] &&
          draft.tweenParticleLookup[targetChapterId][originalId];

        if (originalInLookup) {
          draft.tweenParticleLookup = TweenParticleLookup.addEntryForChapter(
            targetChapterId,
            { [originalId]: newId },
            draft.tweenParticleLookup
          );
        }
      })
    );

    context.dispatch(new ParticleActions.DuplicateParticleSystemSuccess(newId, targetChapterId));
  }

  @Action(ParticleActions.UpdateParticleSystem)
  updateParticleSystem(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.UpdateParticleSystem
  ) {

    const patchedState = immer.produce(context.getState(), (draft: ParticlesStateModel) => {
      // TODO: what happens if particle Id collides?
      draft.particleSystems[action.id] = action.particleSystem;
    });
    // TODO[AC]: force particle systems state check, revisit
    context.patchState({
      ...patchedState,
      particleSystems: {
        ...patchedState.particleSystems
      }
    });
  }


  @Action(ParticleActions.DeleteChapterLookup)
  deleteChapterLookup(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.DeleteChapterLookup
  ) {
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        delete draft.tweenParticleLookup[action.chapterId];
      })
    );
  }

  @Action(ParticleActions.SetLookupForChapter)
  setLookupForChapter(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.SetLookupForChapter
  ) {
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        draft.tweenParticleLookup[action.chapterId] = action.lookup;
      })
    );
  }

  @Action(ParticleActions.AssignParticle)
  assignParticle(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.AssignParticle
  ) {
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        const currentChapterParticles = draft.assignedParticles[action.chapterId] || [];
        const originalId = ParticleIdUtils.getParticleIdRoot(action.id);

        draft.assignedParticles[action.chapterId] = currentChapterParticles.concat(
          action.id
        );
        
        draft.tweenParticleLookup = TweenParticleLookup.addEntryForChapter(
          action.chapterId,
          { [originalId]: action.id },
          draft.tweenParticleLookup
        );
        
        draft.particleSystems = { ...draft.particleSystems };
      })
    );
  }

  @Action(ParticleActions.UnassignParticle)
  unassignParticle(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.UnassignParticle
  ) {
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        const currentChapterParticles = draft.assignedParticles[action.chapterId] || [];
        const originalId = ParticleIdUtils.getParticleIdRoot(action.id);

        draft.assignedParticles[action.chapterId] = currentChapterParticles.filter(
          (id) => id !== action.id
        );

        if (draft.tweenParticleLookup[action.chapterId]) {
          delete draft.tweenParticleLookup[action.chapterId][originalId];
        }
    
        draft.particleSystems = { ...draft.particleSystems };
      })
    );
  }

  @Action(ParticleActions.DeleteParticleSystem)
  deleteParticleSystem(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.DeleteParticleSystem
  ) {

    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {

        // remove the particle
        delete draft.particleSystems[action.id];

        // remove all chapter associations
        Object.keys(draft.assignedParticles).forEach(chapterId => {
          draft.assignedParticles[chapterId] = draft.assignedParticles[chapterId].filter(id => id !== action.id);
        });

        // update lookup
        Object.keys(draft.tweenParticleLookup).forEach(chapterId => {
          delete draft.tweenParticleLookup[chapterId][ParticleIdUtils.getParticleIdRoot(action.id)];
        });

      })
    );
  }

  @Action(ParticleActions.SelectParticleSystem)
  selectParticleSystem(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.SelectParticleSystem
  ) {
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        draft.particleSelection = {
          selectedParticle: action.id
        };
      })
      
    );
  }

  @Action(ParticleActions.SelectParticleConstraint)
  selectParticleConstraint(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.SelectParticleConstraint
  ) {
    const state  = context.getState();
    const particleSelection = state.particleSelection;
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        draft.particleSelection = {
          ...particleSelection,
          selectedConstraint: action.index
        };
      })      
    );
  }

  @Action(ParticleActions.SelectParticleCreator)
  selectParticleCreator(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.SelectParticleCreator
  ) {
    const state  = context.getState();
    const particleSelection = state.particleSelection;
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        draft.particleSelection = {
          ...particleSelection,
          selectedCreator: action.index
        };
      })      
    );
  }

  @Action(ParticleActions.SelectParticleUpdater)
  selectParticleUpdater(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.SelectParticleUpdater
  ) {
    const state  = context.getState();
    const particleSelection = state.particleSelection;
    context.patchState(
      immer.produce(context.getState(), (draft: ParticlesStateModel) => {
        draft.particleSelection = {
          ...particleSelection,
          selectedUpdater: action.index
        };
      })      
    );
  }

  @Action(ParticleActions.UpdateTweenParticleLookupChapters)
  updateTweenParticleLookupChapters(
    context: StateContext<ParticlesStateModel>,
    action: ParticleActions.UpdateTweenParticleLookupChapters
  ) {
    context.patchState(immer.produce(context.getState(), (draft: ParticlesStateModel) => {
      draft.tweenParticleLookup = TweenParticleLookup.updateChapterIds(
        draft.tweenParticleLookup,
        action.chapterMap
      );
    }));
  }


  constructor() { }
}
